import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import { Link as RouterLink } from 'react-router-dom';
import TrainerPaymentFields from 'components/trainer-payment/TrainerPaymentFields';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import SecondaryButtonLoading from 'components/shared/buttons/SecondaryLoadingButton';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import TrainerPaymentListeners from 'components/trainer-payment/TrainerPaymentListeners';
import defaultPaymentFormValidations from 'common/validations/form/defaultPayment';
import { createTrainerPaymentInitialValues } from 'common/form/initial-values/create';
import { trainerCreatePaymentSearchFields } from 'common/constants/query-fields/trainer';
import getDataToSave from 'common/form/dataToSave';
import requestStatus from 'common/constants/status';
import routes from 'common/routes';
import StyledButtonsContainer from 'common/theme/containers/StyledButtonsContainer';
import { onTrainerPaymentFormChange } from 'common/utils/payment';
import AppBarContext from 'context/AppBarContext';
import { handleTrainerIdChange } from 'common/utils/user';
import trainerPaymentSlice from 'store/modules/trainerPayment';

const requiredFields = [
  'created_at',
  'end_date',
  'plan_name',
  'price',
  'sessions_paid',
  'start_date',
  'user_id'
];

function TrainerPaymentCreate() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['buttons', 'routes']);
  const pageTitle = t('payment.trainer.create', { ns: 'routes' });
  const formRef = useRef(null);
  const [formDisabled, setFormDisabled] = useState(false);
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const searchStatus = useSelector((state) => state.trainer.status.search);
  const status = useSelector((state) => state.trainerPayment.status.general);
  const trainerOptions = useSelector((state) => state.trainer.data.search);

  useEffect(() => {
    if (status === requestStatus.success) {
      formRef.current.restart();
    }
  }, [status]);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  const onCreate = useCallback(
    (values) => {
      const dataToSave = getDataToSave(values, 'payment');
      dispatch(trainerPaymentSlice.actions.create(dataToSave));
    },
    [dispatch]
  );

  const onFormChange = useCallback((field, value, previousValue) => {
    if (value !== previousValue) {
      onTrainerPaymentFormChange(field, setFormDisabled, formRef, formDisabled);
    }
  }, []);

  const onTrainerIdChange = handleTrainerIdChange(dispatch, trainerCreatePaymentSearchFields);

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Form
        initialValues={createTrainerPaymentInitialValues}
        onSubmit={onCreate}
        validate={defaultPaymentFormValidations}
        render={({ form, handleSubmit, invalid, pristine }) => {
          formRef.current = form;

          return (
            <form onSubmit={handleSubmit}>
              <TrainerPaymentFields
                isFormDisabled={formDisabled}
                onTrainerIdChange={onTrainerIdChange}
                requiredFields={requiredFields}
                searchStatus={searchStatus}
                trainerOptions={trainerOptions}
              />
              <StyledButtonsContainer>
                <SecondaryButtonLoading
                  component={RouterLink}
                  disabled={status === requestStatus.loading}
                  style={{ marginRight: '32px' }}
                  label={t('cancel', { ns: 'buttons' })}
                  to={routes.listTrainerPayments.administrator.path}
                  type="button"
                />
                <PrimaryLoadingButton
                  disabled={invalid || pristine || status === requestStatus.loading}
                  loading={status === requestStatus.loading}
                  label={t('payment.trainer.create', { ns: 'buttons' })}
                  type="submit"
                />
              </StyledButtonsContainer>
              <TrainerPaymentListeners onFormChange={onFormChange} />
            </form>
          );
        }}
      />
    </>
  );
}

export default TrainerPaymentCreate;
